import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";

import { Button } from "~/components";
import { icons, Modal } from "~/ui";
import { DeleteAllConfirmDialog } from "./delete-all-confirm-dialog";
import { GlobalRulesList } from "./global-rules-list";
import { GlobalRulesUploadScreen } from "./global-rules-upload-screen";
import { useDeleteAllGlobalScheduleRules } from "./use-delete-all-global-schedule-rules";
import {
  useGlobalScheduleRules,
  useInvalidateGlobalScheduleRules,
} from "./use-global-schedule-rules";
import {
  useGlobalScheduleRulesSummary,
  useInvalidateGlobalScheduleRulesSummary,
} from "./use-global-schedule-rules-summary";

type PanelView = "panel" | "upload";

const formatCoverageDay = (isoDate: string) =>
  format(parseISO(isoDate), "MMM d, yyyy");

interface GlobalRulesPanelProps {
  show: boolean;
  onClose: () => void;
  onRulesCountChange: (count: number) => void;
}

export const GlobalRulesPanel = ({
  show,
  onClose,
  onRulesCountChange,
}: GlobalRulesPanelProps) => {
  const {
    data: summary,
    isPending,
    isError: summaryFailed,
    refetch: refetchSummary,
  } = useGlobalScheduleRulesSummary();
  const invalidateSummary = useInvalidateGlobalScheduleRulesSummary();
  const invalidateRules = useInvalidateGlobalScheduleRules();
  const [view, setView] = useState<PanelView | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const rulesCount = summary?.rulesCount ?? 0;
  const latestImport = summary?.latestImport ?? null;
  const activeView: PanelView = view ?? (rulesCount > 0 ? "panel" : "upload");

  const {
    data: rules,
    isPending: isPendingRules,
    isError: rulesFailed,
  } = useGlobalScheduleRules(show && rulesCount > 0);

  const { mutate: deleteAllRules, isPending: isDeleting } =
    useDeleteAllGlobalScheduleRules({
      onSuccess: () => {
        setShowDeleteConfirm(false);
        setView(null);
        void invalidateSummary();
        void invalidateRules();
      },
    });

  useEffect(() => {
    onRulesCountChange(rulesCount);
  }, [rulesCount, onRulesCountChange]);

  const handleImportSuccess = () => {
    void invalidateSummary();
    void invalidateRules();
    setView("panel");
  };

  const handleClose = () => {
    setView(null);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleReplace = () => {
    setView("upload");
  };

  const handleUploadCancel = () => {
    if (rulesCount > 0) {
      setView("panel");
    } else {
      handleClose();
    }
  };

  const showPanel = activeView === "panel" && rulesCount > 0 && !summaryFailed;

  const title = activeView === "panel" ? "Global Rules" : "Edit global rules";
  const description =
    activeView === "panel"
      ? "Block rules applied to all providers"
      : "Upload a CSV file defining global block rules. These rules apply to all providers automatically.";

  return (
    <>
      <Modal
        show={show}
        title={title}
        description={description}
        onClose={handleClose}
        goBack={
          activeView === "upload" && rulesCount > 0
            ? () => setView("panel")
            : undefined
        }
        hideCloseButton={!showPanel}
        handleConfirm={showPanel ? handleReplace : undefined}
        confirmLabel="Replace CSV"
      >
        {isPending ? (
          <div className="space-y-4">
            <div className="h-20 animate-pulse rounded-xl bg-neutral-100" />
            <div className="h-24 animate-pulse rounded-xl bg-neutral-100" />
          </div>
        ) : summaryFailed ? (
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">
              Could not load the global rules
            </p>
            <p className="mt-1 text-sm text-red-700">
              Retry before uploading a file. Uploading now would replace rules
              this panel cannot show.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetchSummary()}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        ) : showPanel ? (
          <>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Current CSV file
              </p>

              <div className="mb-5 flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-xs font-bold text-violet-700">
                  CSV
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-800">
                    {latestImport?.filename ?? "Global rules CSV"}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {rulesCount} active rule{rulesCount !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Replace CSV"
                    aria-label="Replace CSV"
                    onClick={handleReplace}
                    className="size-8 rounded-lg border border-neutral-200 bg-white text-neutral-500 hover:bg-violet-50"
                  >
                    <icons.outline.PencilIcon className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete all rules"
                    aria-label="Delete all rules"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="size-8 rounded-lg border border-neutral-200 bg-white text-neutral-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <icons.outline.TrashIcon className="size-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-violet-700">
                  Active rules summary
                </p>
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-neutral-600">
                    Total block rules
                  </span>
                  <span className="text-sm font-semibold text-neutral-800">
                    {rulesCount}
                  </span>
                </div>
                {latestImport && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-neutral-600">
                      Providers with exceptions
                    </span>
                    <span className="text-sm font-semibold text-neutral-800">
                      {latestImport.providersWithExceptionsCount}
                    </span>
                  </div>
                )}
                {latestImport?.dateRange && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-neutral-600">Coverage</span>
                    <span className="text-sm font-semibold text-neutral-800">
                      {formatCoverageDay(latestImport.dateRange.start)} -{" "}
                      {formatCoverageDay(latestImport.dateRange.end)}
                    </span>
                  </div>
                )}

                <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-violet-700">
                  Rules
                </p>
                <GlobalRulesList
                  rules={rules ?? []}
                  isPending={isPendingRules}
                  isError={rulesFailed}
                />
              </div>
            </div>
          </>
        ) : (
          <GlobalRulesUploadScreen
            onSuccess={handleImportSuccess}
            onCancel={handleUploadCancel}
          />
        )}
      </Modal>

      <DeleteAllConfirmDialog
        show={showDeleteConfirm}
        rulesCount={rulesCount}
        isLoading={isDeleting}
        onConfirm={() => deleteAllRules()}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </>
  );
};
